import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, CheckCircle } from '../icons/wp-icons';
import apiClient from '../api/apiClient';
import { getHostBranding } from '../utils/hostBranding';
import HostBrandedNotice from '../components/HostBrandedNotice';

function ForgotPassword() {
  const hostBranding = useMemo(() => getHostBranding(), []);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Theme-Color setzen (nur wenn wir nicht auf einem Whitelabel-Host sind —
  // der HostBrandedNotice-Pfad unten setzt seine eigene Theme-Color)
  useEffect(() => {
    if (hostBranding) return;
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    const originalColor = metaThemeColor?.getAttribute('content');

    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', '#378ab2');
    }

    return () => {
      if (metaThemeColor && originalColor) {
        metaThemeColor.setAttribute('content', originalColor);
      }
    };
  }, [hostBranding]);

  // Auf Whitelabel-Hosts (HeyKiki etc.) ist der Passwort-Reset extern verwaltet —
  // das WerkPilot-Email-Flow würde den falschen Absender/Brand zeigen.
  if (hostBranding?.passwordFlowHandledExternally) {
    return (
      <HostBrandedNotice
        hostBranding={hostBranding}
        pageTitle="Passwort vergessen"
        title="Passwort-Reset"
        message={`Passwörter werden bei ${hostBranding.supportContact} verwaltet. Bitte wende dich für das Zurücksetzen deines Passworts direkt an den ${hostBranding.supportContact}-Support.`}
      />
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await apiClient.post('/auth/forgot-password', { email });
      setSuccess(true);
    } catch (err) {
      // API gibt immer success zurueck, aber bei Netzwerkfehlern zeigen wir eine Meldung
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="bg-surface p-8 rounded-card shadow-card w-full max-w-md text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>

          <h2 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            E-Mail gesendet
          </h2>

          <p className="text-text-muted mb-6">
            Falls ein Konto mit der Adresse <strong>{email}</strong> existiert,
            haben wir Ihnen einen Link zum Zurücksetzen Ihres Passworts gesendet.
          </p>

          <p className="text-sm text-text-muted mb-6">
            Bitte prüfen Sie auch Ihren Spam-Ordner. Der Link ist 1 Stunde gültig.
          </p>

          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-[#378ab2] hover:underline font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück zur Anmeldung
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="bg-surface p-8 rounded-card shadow-card w-full max-w-md">
        <div className="text-center mb-6">
          <img
            src="/icons/icon-512x512.png"
            alt="WerkPilot Logo"
            className="w-24 h-24 mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Passwort vergessen?
          </h1>
          <p className="text-text-muted mt-2">
            Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen einen Link zum Zurücksetzen.
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">E-Mail-Adresse</label>
            <div className="relative">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 pl-11 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#378ab2]"
                placeholder="ihre@email.de"
                required
                disabled={loading}
                autoFocus
              />
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            </div>
          </div>

          <button
            type="submit"
            className="w-full bg-[#378ab2] text-white py-3 rounded-button font-medium hover:bg-[#2a6d8a] transition disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading || !email}
          >
            {loading ? 'Wird gesendet...' : 'Link anfordern'}
          </button>
        </form>

        <div className="mt-6 pt-6 border-t border-border text-center">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-[#378ab2] hover:underline font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück zur Anmeldung
          </Link>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;
